import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap } from '../lib/api-envelope';
import type { QboReadiness } from '../types/qbo-mapping';
import { useInvoicePermissions } from './useInvoicePermissions';

/**
 * Under `['qbo']` on purpose: every mapping save invalidates `['qbo']`, so
 * mapping the last blocker refreshes this without anyone remembering to.
 */
export const QBO_READINESS_KEY = ['qbo', 'readiness'] as const;

/**
 * `GET /quickbooks/readiness` (API #887) — is this company set up to push at
 * all? One hook, so the mapping screen, the invoice detail and the invoice list
 * read one cache entry and cannot disagree.
 *
 * Enabled only for the push's own roles (admin / office_staff, strict): the
 * route 403s everyone else, and they have no push button to gate.
 */
export function useQboReadiness() {
  const { canPushQbo } = useInvoicePermissions();
  return useQuery({
    queryKey: QBO_READINESS_KEY,
    enabled: canPushQbo,
    queryFn: async (): Promise<QboReadiness> => {
      const res = await api.quickbooks.readiness();
      const data = unwrap<QboReadiness>(res.data);
      // A missing payload is a load failure, never "not ready" and never "ready".
      if (!data) throw new Error('QuickBooks readiness came back empty');
      return data;
    },
  });
}

export type QboPushGate =
  | { enabled: true; reason: null }
  | { enabled: false; reason: 'loading' | 'error' | 'not_connected' | 'not_ready' };

/**
 * Whether a Push button may be clicked, and why not.
 *
 * DISABLED WHILE LOADING. The opposite of `useTruckingFeature`'s "true while
 * loading", deliberately: a button that renders enabled and then disables
 * invites exactly the click this gate exists to stop. Only an explicit
 * `pushEnabled === true` from the server enables it.
 */
export function qboPushGate(q: { data?: QboReadiness; isError: boolean }): QboPushGate {
  if (q.data?.pushEnabled === true) return { enabled: true, reason: null };
  if (q.isError) return { enabled: false, reason: 'error' };
  if (!q.data) return { enabled: false, reason: 'loading' };
  if (!q.data.connected) return { enabled: false, reason: 'not_connected' };
  return { enabled: false, reason: 'not_ready' };
}
